import { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { motion } from 'framer-motion';
import {
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import type { Conversion } from '@types';
import { converterApi } from '@services/api';
import ConversionProgress from './ConversionProgress';

interface ConversionStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversionId: string;
  onDownload?: (conversionId: string) => void;
  onRetry?: (conversionId: string) => void;
}

const POLL_INTERVAL = 2000; // 2 seconds

export default function ConversionStatusModal({
  isOpen,
  onClose,
  conversionId,
  onDownload,
  onRetry,
}: ConversionStatusModalProps) {
  const [conversion, setConversion] = useState<Conversion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState<
    number | undefined
  >();

  useEffect(() => {
    if (!isOpen || !conversionId) {
      return;
    }

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const fetchConversionStatus = async () => {
      try {
        const data = await converterApi.getConversionStatus(conversionId);
        setConversion(data);
        setError(null);
        setLoading(false);

        // Calculate estimated time remaining (simple heuristic)
        if (data.status === 'PROCESSING' && data.estimated_records) {
          const recordsProcessed = data.record_count || 0;
          const totalRecords = data.estimated_records;
          const processingTime = data.processing_time_seconds || 0;

          if (recordsProcessed > 0 && processingTime > 0) {
            const timePerRecord = processingTime / recordsProcessed;
            const remainingRecords = totalRecords - recordsProcessed;
            const estimated = Math.ceil(remainingRecords * timePerRecord);
            setEstimatedTimeRemaining(estimated);
          }
        } else {
          setEstimatedTimeRemaining(undefined);
        }

        // Stop polling if conversion is in a terminal state
        if (
          data.status === 'COMPLETED' ||
          data.status === 'FAILED' ||
          data.status === 'CANCELLED'
        ) {
          if (intervalId) {
            clearInterval(intervalId);
          }
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to fetch conversion status'
        );
        setLoading(false);
        if (intervalId) {
          clearInterval(intervalId);
        }
      }
    };

    // Initial fetch
    fetchConversionStatus();

    // Set up polling
    intervalId = setInterval(fetchConversionStatus, POLL_INTERVAL);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isOpen, conversionId]);

  const handleDownload = () => {
    if (conversion && onDownload) {
      onDownload(conversion.id);
    }
  };

  const handleRetry = () => {
    if (conversion && onRetry) {
      onRetry(conversion.id);
      onClose();
    }
  };

  const getProgress = (): number => {
    if (!conversion) return 0;

    switch (conversion.status) {
      case 'PENDING':
        return 10;
      case 'PROCESSING':
        if (conversion.estimated_records && conversion.record_count) {
          return Math.min(
            90,
            Math.floor(
              (conversion.record_count / conversion.estimated_records) * 100
            )
          );
        }
        return 50;
      case 'COMPLETED':
        return 100;
      case 'FAILED':
      case 'CANCELLED':
        return 0;
      default:
        return 0;
    }
  };

  const getStatusMessage = (): string => {
    if (!conversion) return '';

    switch (conversion.status) {
      case 'PENDING':
        return 'Your conversion is queued and will begin shortly...';
      case 'PROCESSING':
        if (conversion.record_count && conversion.estimated_records) {
          return `Processing ${conversion.record_count} of ${conversion.estimated_records} records...`;
        }
        return 'Processing your file...';
      case 'COMPLETED':
        return `Successfully converted ${conversion.record_count || 0} records in ${conversion.processing_time_seconds || 0}s`;
      case 'FAILED':
        return 'Conversion failed. Please check the error details below.';
      case 'CANCELLED':
        return 'Conversion was cancelled.';
      default:
        return '';
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Conversion Status
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-full p-1 hover:bg-gray-100 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                  </button>
                </div>

                {/* Content */}
                {loading && !conversion ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
                  </div>
                ) : error ? (
                  <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                    <div className="flex items-start space-x-3">
                      <ExclamationCircleIcon className="h-5 w-5 text-red-500 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-medium text-red-800">
                          Error
                        </h4>
                        <p className="text-sm text-red-700 mt-1">{error}</p>
                      </div>
                    </div>
                  </div>
                ) : conversion ? (
                  <div className="space-y-6">
                    {/* File Info */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-gray-900 mb-2">
                        File Details
                      </p>
                      <dl className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <dt className="text-gray-500">File Name:</dt>
                          <dd className="text-gray-900 font-medium truncate ml-2">
                            {conversion.file_name}
                          </dd>
                        </div>
                        <div className="flex justify-between text-sm">
                          <dt className="text-gray-500">Source System:</dt>
                          <dd className="text-gray-900 font-medium">
                            {conversion.source_system}
                          </dd>
                        </div>
                        <div className="flex justify-between text-sm">
                          <dt className="text-gray-500">Started:</dt>
                          <dd className="text-gray-900 font-medium">
                            {new Date(conversion.started_at).toLocaleString()}
                          </dd>
                        </div>
                      </dl>
                    </div>

                    {/* Progress */}
                    <ConversionProgress
                      progress={getProgress()}
                      status={conversion.status}
                      statusMessage={getStatusMessage()}
                      estimatedTimeRemaining={estimatedTimeRemaining}
                      showCancel={false}
                    />

                    {/* Error Message */}
                    {conversion.status === 'FAILED' &&
                      conversion.error_message && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="rounded-lg bg-red-50 border border-red-200 p-4"
                        >
                          <div className="flex items-start space-x-3">
                            <ExclamationCircleIcon className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-red-800">
                                Conversion Error
                              </h4>
                              <p className="text-sm text-red-700 mt-1">
                                {conversion.error_message}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      )}

                    {/* Success Message */}
                    {conversion.status === 'COMPLETED' && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="rounded-lg bg-green-50 border border-green-200 p-4"
                      >
                        <div className="flex items-start space-x-3">
                          <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5" />
                          <div>
                            <h4 className="text-sm font-medium text-green-800">
                              Conversion Completed
                            </h4>
                            <p className="text-sm text-green-700 mt-1">
                              Your file has been successfully converted and is
                              ready for download.
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Actions */}
                    <div className="flex space-x-3 pt-4 border-t border-gray-200">
                      {conversion.status === 'COMPLETED' && onDownload && (
                        <button
                          type="button"
                          onClick={handleDownload}
                          className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                          Download File
                        </button>
                      )}

                      {conversion.status === 'FAILED' && onRetry && (
                        <button
                          type="button"
                          onClick={handleRetry}
                          className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <ArrowPathIcon className="h-5 w-5 mr-2" />
                          Retry Conversion
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={onClose}
                        className={`${
                          conversion.status === 'COMPLETED' ||
                          conversion.status === 'FAILED'
                            ? 'flex-1'
                            : 'w-full'
                        } inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                ) : null}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
